<template>
  <v-card border>
    <template #title> {{ $t('dashboard.marketing-topics.title') }} </template>
    <template #text>
      <div class="dashboard-markero-topics__header">
        <span class="text-subtitle-1">
          {{
            $t('dashboard.marketing-topics.subheadline', {
              count: createdTopicsAmount,
              limit: maxActiveTopics,
            })
          }}
        </span>
        <v-btn
          v-if="isBeginner"
          :to="{ name: 'Settings', params: { section: 'contract' }, query: { showUpgrade: '1' } }"
          variant="tonal"
          size="small"
        >
          {{ $t('dashboard.marketing-topics.upgrade') }}
        </v-btn>
      </div>
      <div class="dashboard-markero-topics__list">
        <template v-if="hasTopics">
          <p class="font-weight-bold">{{ $t('dashboard.marketing-topics.most-used-topics') }}</p>
          <ul class="dashboard-markero-topics__list-wrapper">
            <li
              v-for="(item, index) in listItems"
              :key="index"
              :class="{
                'dashboard-markero-topics__list-item': true,
                'dashboard-markero-topics__list-item--border': !index,
                'dashboard-markero-topics__list-item--clickable': !!index,
              }"
              @click="!!index && createContentWithTopic(item[0].topicId)"
            >
              <div
                v-for="header in item"
                :key="`${index}-${header.value}`"
                :style="header?.styles"
                :class="header?.class"
              >
                {{ header.title }}
              </div>
            </li>
          </ul>
        </template>
        <div
          v-else
          class="d-flex gap-1"
        >
          <v-icon
            icon="mdi-lightbulb"
            color="primary"
            size="x-large"
          />
          <p>
            {{
              $t('dashboard.marketing-topics.many-topics', {
                count: maxActiveTopics - createdTopicsAmount,
              })
            }}
          </p>
        </div>
        <div class="dashboard-markero-topics__list-actions">
          <v-btn
            color="primary"
            @click="createAdditionalTopic"
          >
            {{ $t('dashboard.marketing-topics.create-additional-topic') }}
          </v-btn>
        </div>
      </div>
    </template>
  </v-card>
</template>
<script lang="ts" setup>
import { useQuery } from '@vue/apollo-composable'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import useFeature from '@/composables/useFeature'
import { ProductType } from '@/modules/productPackages/enums/ProductType'
import GET_TOPICS from '@/modules/topics/graphql/getTopics.gql'

import GET_TOP_TOPICS_COMBINED_USAGE from './getTopTopicsCombinedUsage.gql'

type Topic = {
  topicId: string
  topicName: string
  generatedTextCount: number
  postedContentCount: number
}

const router = useRouter()
const { t } = useI18n()

const $auth = inject('auth')

const { getFeature } = useFeature()

const { result: topTopicsResult } = useQuery(GET_TOP_TOPICS_COMBINED_USAGE)
const { result: topicsResult } = useQuery(GET_TOPICS)

const isBeginner = computed(() => $auth.user.productType === ProductType.BEGINNER)

const maxActiveTopics = computed(() => getFeature('topic')?.config?.maxActiveTopics)
const createdTopicsAmount = computed(
  () => topicsResult.value?.topics.filter((topic) => topic.status !== 'ARCHIVED').length || 0
)

const topics = computed(() => {
  const result: Topic[] = topTopicsResult.value?.getTopTopicsCombinedUsage || []

  return result.map((item, index: number) => {
    return [
      {
        title: index + 1,
        value: 'id',
        class: 'text-primary font-weight-bold',
        topicId: item.topicId,
      },
      { title: item.topicName, value: 'topic', class: 'text-truncate' },
      {
        title: item.generatedTextCount,
        value: 'created_topics',
        styles: 'text-align:center',
      },
      { title: item.postedContentCount, value: 'posts', styles: 'text-align:end', class: 'pr-3' },
    ]
  })
})

const hasTopics = computed(() => topics.value.length > 2)

const listItems = computed(() => {
  const headers = [
    { title: '#', value: 'id' },
    { title: t('dashboard.marketing-topics.list.topic'), value: 'topic' },
    {
      title: t('dashboard.marketing-topics.list.created-content'),
      value: 'created_topics',
      styles: 'text-wrap: nowrap',
    },
    { title: t('dashboard.marketing-topics.list.posts'), value: 'posts', styles: 'text-align:end' },
  ]

  return [headers, ...topics.value]
})

const createAdditionalTopic = () => router.push('/topics')

const createContentWithTopic = (topicId: string) => {
  router.push({ name: 'content-creator', query: { topicId } })
}
</script>
<style scoped>
.dashboard-markero-topics__list-item {
  display: grid;
  grid-template-columns: 30px minmax(200px, 300px) 1fr 1fr;
  grid-gap: 8px;
  padding: 8px 4px;
}
.dashboard-markero-topics__list-item--clickable {
  cursor: pointer;
}
.dashboard-markero-topics__list-item--clickable:hover {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.dashboard-markero-topics__list-wrapper {
  display: flex;
  flex-direction: column;
}
.dashboard-markero-topics__list-item--border {
  border-bottom: 1px solid black;
}
.dashboard-markero-topics__list-actions {
  display: flex;
  justify-content: end;
  padding-top: 16px;
}
.dashboard-markero-topics__header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}
</style>
